import { useState, useEffect, useCallback } from 'react';

/**
 * A custom hook to handle theme toggling functionality
 */
const useThemeToggle = () => {
  const [isDark, setIsDark] = useState(false);

  // Initialize state on component mount
  useEffect(() => {
    setIsDark(document.documentElement.classList.contains('dark'));
  }, []);

  const handleToggleTheme = () => {
    const newIsDark = !isDark;

    // Toggle the class
    if (newIsDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    // Save preference to localStorage for persistence
    localStorage.setItem('theme', newIsDark ? 'dark' : 'light');

    // Update state for the icon
    setIsDark(newIsDark);
  };

  // Theme refresh handler function to check and apply saved theme
  const themeRefreshHandler = useCallback(() => {
    // Check if we have a saved theme preference in localStorage
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'dark') {
      // Apply dark theme if it was saved
      document.documentElement.classList.add('dark');
      setIsDark(true);
    } else if (savedTheme === 'light') {
      // Ensure dark theme is removed if light was saved
      document.documentElement.classList.remove('dark');
      setIsDark(false);
    }
    // No else case - if no preference is saved, we keep whatever the default is
  }, []);

  // Apply theme on hook initialization
  useEffect(() => {
    themeRefreshHandler();
  }, [themeRefreshHandler]);

  return {
    isDark,
    handleToggleTheme,
    themeRefreshHandler,
  };
};

export default useThemeToggle;
